import React from "react";
import { useState } from "react";
import { X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useApp } from '@/context/AppContext';
import { format, getDate } from 'date-fns';

interface NewAddTaskModalProps {
    open: boolean;
    onClose: () => void;
    preselectedProjectId?: string;
    preselectedDate?: Date;
}

export default function AddTask({ open, onClose, preselectedProjectId, preselectedDate }: NewAddTaskModalProps) {

    const { projects, addTask } = useApp();

    const [selectedDate, setSelectedDate] = useState<Date | null>(preselectedDate || new Date());
    const [newTaskProjectId, setNewTaskProjectId] = useState(preselectedProjectId || 'none');
    const [newTaskTitle, setNewTaskTitle] = useState('');
    const [newTaskNotes, setNewTaskNotes] = useState('');
    const [showAddTask, setShowAddTask] = useState(true);

    const handleAddTask = () => {
        if (!newTaskTitle.trim() || !newTaskProjectId || !selectedDate) return;

        addTask({
            id: `t-${Date.now()}`,
            projectId: newTaskProjectId,
            title: newTaskTitle.trim(),
            notes: newTaskNotes.trim() || undefined,
            scheduledDate: format(selectedDate, 'yyyy-MM-dd'),
            isCompleted: false
        });

        setNewTaskTitle('');
        setNewTaskNotes('');
        setNewTaskProjectId('');
        setShowAddTask(false);
        handleClose();
    };

    const handleClose = () => {
        setShowAddTask(false);
        onClose();
    }

    if (!open) return null;

    return (
        <div className="fixed inset-0 bg-foreground/50 z-50 flex items-end md:items-center justify-center">
            <div className="bg-card w-full md:w-[500px] md:max-h-[90vh] rounded-t-2xl md:rounded-2xl overflow-hidden animate-slide-up shadow-soft">
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-border">
                    <h2 className="text-lg font-bold">משימה חדשה</h2>
                    <button
                        onClick={handleClose}
                        className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-secondary transition-colors disabled:opacity-50"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>
                {/* Content */}
                <div className="p-5 max-h-[70vh] overflow-y-auto">
                    <div className="space-y-6">
                        <div>
                            <label className="block text-sm font-medium mb-3">פרויקט</label>
                            <Select value={newTaskProjectId} onValueChange={setNewTaskProjectId} >
                                <SelectTrigger className="text-right">
                                    <SelectValue placeholder="בחר פרויקט" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem key="none" value="none">בחר פרויקט</SelectItem>
                                    {projects.map(p => (
                                        <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-3">כותרת</label>
                            <div>
                                <Input
                                    value={newTaskTitle}
                                    onChange={(e) => setNewTaskTitle(e.target.value)}
                                    placeholder="הזן כותרת משימה..."
                                    className="text-right"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="text-sm font-medium mb-2 block">הערות (אופציונלי)</label>
                            <Input
                                value={newTaskNotes}
                                onChange={(e) => setNewTaskNotes(e.target.value)}
                                placeholder="הוסף הערות..."
                                className="text-right"
                            />
                        </div>
                        <div>
                            <label className="text-sm font-medium mb-2 block">תאריך</label>
                            <input
                                type="date"
                                value={selectedDate ? format(selectedDate, 'yyyy-MM-dd') : ''}
                                onChange={(e) => setSelectedDate(e.target.value ? new Date(e.target.value) : null)}
                                className="w-full px-4 py-3 rounded-xl border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                                />
                        </div>
                        <div className="flex gap-2 pt-2">
                            <Button
                                onClick={handleAddTask}
                                disabled={!newTaskTitle.trim() || !newTaskProjectId}
                                className="flex-1"
                            >
                                שמור
                            </Button>
                            <Button variant="outline" onClick={handleClose} className="flex-1">
                                ביטול
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}